<template>
  <div class="centered column-container">
    <!-- Show loading spinner if loading -->
    <LoadingComponent v-if="isLoading" />
    <!-- Show convenience fee message -->
    <ConvenienceFeeMessageComponent />

    <div v-if="activeCards.length > 0">
      <select
        id="achDropdown"
        v-model="selectedCardPaymentMethod"
      >
        <option :value="null">
          Select Saved Card
        </option>
        <option
          v-for="card in activeCards"
          :key="card.token"
          :value="card"
        >
          {{ card.cardType }} - Ending in {{ card.last4 }} Exp. {{ card.expirationDate }}
        </option>
        <option :value="null">
          Enter New Card
        </option>
      </select>
    </div>

    <!-- Show hosted fields form -->
    <div
      v-show="!isLoading"
      class="hosted-fields-container"
    >
      <div class="row">
        <div class="cell">
          <label
            v-if="showCreditCardLimitDisclaimer()"
            for="pay-amount"
          >Amount (${{ invoice.remaing_digital_wallet_amount }} limit)</label>
          <label
            v-else
            for="pay-amount"
          >Amount</label>
          <input
            id="pay-amount"
            v-model="customAmount"
            class="input amount-input"
            name="pay-amount"
            type="number"
            @change="customAmountChanged"
          >
          <label
            v-if="showCreditCardLimitDisclaimer()"
            class="amount-disclaimer"
          >Credit Card and digital wallets are limited to $15,000. Balance will be due by Zelle, ACH, or Wire Transfer.</label>
        </div>
      </div>
      <div v-show="showAddCardForm">
        <div class="row">
          <div class="cell">
            <label for="card-number">Card number</label>
            <div
              id="card-number"
              class="input"
            />
          </div>
        </div>
        <div class="row">
          <div class="cell">
            <!-- This input is non-standard to braintree, but we require it. We validate manually. -->
            <label for="street-address">Street address</label>
            <div
              id="street-address"
              class="input"
            >
              <input
                name="street-address"
                autocomplete="off"
                :class="{ valid: validation.streetAddress === true }"
                @change="onStreetAddressChange"
                @blur="validate"
              >
            </div>
          </div>
        </div>
        <div class="row">
          <div class="cell postal-code-cell">
            <label for="postal-code">Postal code</label>
            <div
              id="postal-code"
              class="input"
            />
          </div>
          <div class="cell">
            <label for="expiration-date">Expiration date</label>
            <div
              id="expiration-date"
              class="input"
            />
          </div>
          <div class="cell">
            <label for="cvv">Security code</label>
            <div
              id="cvv"
              class="input"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Show error -->
    <BraintreeErrorMessageComponent
      v-if="error"
      :error="error"
    />
    <!-- End CreditCardComponent -->
  </div>
</template>
<script>
import BraintreeError from '../../../../errors/BraintreeError'
import BraintreeErrorMessageComponent from '../../Shared/BraintreeErrorMessageComponent.vue'
import LoadingComponent from '../../Shared/LoadingComponent.vue'
import ConvenienceFeeMessageComponent from '../../Shared/ConvenienceFeeMessageComponent.vue'
export default {
	name: 'CreditCardComponent',
	components: {
		BraintreeErrorMessageComponent,
		LoadingComponent,
		ConvenienceFeeMessageComponent
	},
	props: ['braintreeInstance', 'invoice', 'disablePaymentMethod', 'deviceData'],
	data: function () {
		return {
			isLoading: false,
			isSubmitting: false,
			error: null,
			defaultErrorMessage: 'Unable to process payment. Please try again.',
			localHostedFieldInstance: null,
			validation: {
				number: false,
				cvv: false,
				expirationDate: false,
				postalCode: false,
				streetAddress: false
			},
			customAmount: 0,
			hostedFields: {
				number: {
					container: '#card-number',
					placeholder: ''
				},
				cvv: {
					container: '#cvv',
					placeholder: ''
				},
				expirationDate: {
					container: '#expiration-date',
					placeholder: 'MM/YY'
				},
				postalCode: {
					container: '#postal-code',
					placeholder: ''
				}
			},
			hostedFieldsStyles: {
				input: {
					'font-size': '14px',
					'border-radius': '0.5em',
					border: '1px solid rgba(51, 51, 51, 0.50)',
					background: 'rgba(243, 243, 243, 0.50)'
				},
				'input.invalid': {
					color: 'red'
				},
				'input.valid': {
					color: 'green'
				}
			},
			activeCards: [],
			showAddCardForm: true,
			selectedCardPaymentMethod: null
		}
	},
	computed: {
		paymentFormStore () {
			return this.$store.getters.paymentForm
		}
	},
	watch: {
		paymentFormStore: function () {
			// Review button has been clicked from PaymentCollectionComponent, try and get a nonce
			if (this.paymentFormStore && this.paymentFormStore.type === 'credit_card' && this.paymentFormStore.submitCC === true && (this.paymentFormStore.nonce === undefined || this.paymentFormStore.nonce === null)) {
				const paymentForm = {
					...this.paymentFormStore,
					submitCC: false
				}

				this.$store.dispatch('updatePaymentForm', paymentForm)

				this.fetchBraintreeNonce()
			}
		},
		selectedCardPaymentMethod: function () {
			// Adding new card
			if (this.selectedCardPaymentMethod == null) {
				this.showAddCardForm = true
				let paymentForm = {
					nonce: null,
					type: 'credit_card',
					submitCC: false,
					deviceData: this.deviceData,
					savedPaymentMethod: false
				}

				this.$store.dispatch('updatePaymentForm', paymentForm)
			} else {
				// Selecting existing card
				this.showAddCardForm = false
				let paymentForm = {
					nonce: this.selectedCardPaymentMethod.token,
					type: 'credit_card',
					submitCC: false,
					deviceData: this.deviceData,
					savedPaymentMethod: true,
					isPartialPayment: this.invoice.invoice_balance != this.customAmount,
					amount: this.customAmount
				}

				this.$store.dispatch('updatePaymentForm', paymentForm)
			}
		}
	},
	created () {
		this.init()
		this.getActiveCards()
	},
	methods: {
		/**
     * Init the credit card form
     */
		init: function () {
			this.isLoading = true

			// Create the hosted fields
			window.braintree.hostedFields.create(
				{
					client: this.braintreeInstance,
					preventAutofill: true,
					styles: this.hostedFieldsStyles,
					fields: this.hostedFields
				},
				(hostedFieldsErr, hostedFieldsInstance) => {
					if (hostedFieldsErr) {
						const exception = new BraintreeError(hostedFieldsErr)
						this.error = exception.message
						this.isLoading = false
						return
					}

					this.localHostedFieldInstance = hostedFieldsInstance
					// Update our data.validation object
					this.localHostedFieldInstance.on(
						'validityChange',
						this.validityChange
					)

					if (this.invoice !== undefined) {
						this.customAmount = this.invoice.remaing_digital_wallet_amount ? this.invoice.remaing_digital_wallet_amount.toFixed(2) : 0
					}

					// Validate and submit for nonce on: blur, inputSubmitRequest (enter key pressed)
					this.localHostedFieldInstance.on('blur', this.validate)
					this.localHostedFieldInstance.on('inputSubmitRequest', this.validate)
					this.isLoading = false
				}
			)

			// this.getActiveCards()
		},
		customAmountChanged: function (event) {
			// Only reset the nonce if we are not using a vaulted card
			if (this.paymentFormStore.nonce !== null && this.selectedCardPaymentMethod === null) {
				this.$store.dispatch('updatePaymentForm', {
					...this.paymentFormStore,
					nonce: null
				})
			}
			const current = event.target.value
			const amount = parseFloat(current)
			if (amount > this.invoice.remaing_digital_wallet_amount) {
				this.customAmount = this.invoice.remaing_digital_wallet_amount
			} else {
				this.customAmount = amount
			}

			if (this.selectedCardPaymentMethod !== null) {
				let paymentForm = {
					nonce: this.selectedCardPaymentMethod.token,
					type: 'credit_card',
					submitCC: false,
					deviceData: this.deviceData,
					savedPaymentMethod: true,
					isPartialPayment: this.invoice.invoice_balance != this.customAmount,
					amount: this.customAmount
				}

				this.$store.dispatch('updatePaymentForm', paymentForm)
			} else {
				let paymentForm = {
					nonce: null,
					type: 'credit_card',
					submitCC: false,
					deviceData: this.deviceData,
					savedPaymentMethod: false,
					isPartialPayment: this.invoice.invoice_balance != this.customAmount,
					amount: this.customAmount
				}

				this.$store.dispatch('updatePaymentForm', paymentForm)
			}
		},
		showCreditCardLimitDisclaimer: function () {
			return this.invoice.invoice_total > 15000
		},
		/**
     * Handle validity change event from street address field.
     * @param {*} event
     */
		onStreetAddressChange: function (event) {
			const { target } = event
			const { value } = target
			this.validityChange({
				emittedBy: 'streetAddress',
				fields: {
					streetAddress: {
						isValid: value.length > 0,
						isPotentiallyValid: value.length > 0
					}
				}
			})
		},
		/**
     * Get value from street address input.
     */
		getStreetAddressInput: function () {
			return document.querySelector('#street-address input')
		},
		/**
     * Fetch the braintree nonce.
     */
		getActiveCards: async function () {
			let apiPayload = {}
			apiPayload = {}
			apiPayload.apiEndpoint = 'account'
			apiPayload.postData = {}
			apiPayload.postData.action = 'get_active_cards'
			apiPayload.postData.customer_id = this.invoice.customer_id
			const response = await this.$store.dispatch('apiCall', apiPayload).catch((error) => {
				console.error(error)
			})
			if (!response || response.status !== 200) {
				return
			}
			if (response.data.payload.active_cards === true && response.data.payload.credit_cards.length > 0) {
				this.activeCards = response.data.payload.credit_cards
			}
		},
		fetchBraintreeNonce: async function () {
			if (this.paymentFormStore.nonce) {
				// We already have it, return.
				return
			}
			this.error = null
			this.isSubmitting = true
			let payload
			try {
				payload = await this.localHostedFieldInstance.tokenize()
			} catch (err) {
				const exception = new BraintreeError(err)
				this.error = exception.message
				this.isSubmitting = false
				return
			}

			if (!payload) {
				this.isSubmitting = false
				this.error = this.defaultErrorMessage
				return
			}

			// Send the nonce to server.
			const streetAddressInput = this.getStreetAddressInput()
			const paymentForm = {
				nonce: payload.nonce,
				type: 'credit_card',
				streetAddress: streetAddressInput ? streetAddressInput.value : null,
				binData: payload.binData,
				deviceData: this.deviceData,
				isPartialPayment: this.invoice.invoice_balance != this.customAmount,
				amount: this.customAmount
			}

			await this.$store.dispatch('updatePaymentForm', paymentForm)
			this.isSubmitting = false

			const continueBtn = document.querySelector('.continue-btn')
			if (continueBtn) {
				continueBtn.focus()
			}
		},
		/**
     * Reset our nonce, sterrt address, and binData
     */
		reset: async function () {
			await this.$store.dispatch('updatePaymentForm', {
				...this.paymentFormStore,
				nonce: null,
				streetAddress: null,
				binData: null
			})
		},
		/**
     * Check if the form is valid.
     */
		isValid: function () {
			const isValid = Object.values(this.validation).every((value) => value)
			return isValid
		},
		/**
     * Validate the form and get the nonce.
     */
		validate: function () {
			const isValid = this.isValid()
			if (
				isValid &&
        !this.isSubmitting &&
        !this.paymentFormStore.nonce &&
        this.paymentFormStore.type === 'credit_card'
			) {
				// Go get the nonce
				// this.fetchBraintreeNonce()
			}
		},
		/**
     * To be called when a field's value changes.
     * Hosted fields will have for us: cvv, expirationDate, number, postalCode
     * We will check street address manually.
     */
		validityChange: function (event) {
			const { emittedBy, fields } = event
			const { isValid, isPotentiallyValid } = fields[emittedBy]
			this.validation[emittedBy] = isValid || isPotentiallyValid
			if (isValid === false && this.paymentFormStore.nonce) {
				this.reset()
			}
		},
		/**
     * To force a payment method to be hidden from list.
     */
		disable: function () {
			this.disablePaymentMethod(this.$options.name)
		}
	}
}
</script>
<style scoped>
.amount-disclaimer {
  text-align: center;
  padding-top: 0.5rem;
  padding-bottom: 1rem;
  font-size: 0.8em;
}
.hosted-fields-container {
  max-width: 400px;
  margin: 0 auto;
}
.hosted-fields-container label {
  line-height: 1.5em;
}
.hosted-fields-container .input {
  width: 100%;
  height: 40px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}
.hosted-fields-container .amount-input {
  background-color: #ffffff;
  margin-bottom: 0;
}
.postal-code-cell {
    flex-basis: 70%;
}
.column-container {
  flex-direction: column;
  position: relative;
}
.row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 10px;
}
.cell {
  margin-right: 10px;
  width: stretch;
}
#street-address {
  padding-top: 5px;
}
#street-address input {
  border-radius: 0;
  border: none;
  outline: 0;
  width: 100%;
}
#street-address input.valid {
  color: green;
}
</style>
